import { ChartInfoItemContent, ChartMissingDataContent } from '@twaice-fe/frontend/shared/ui-components';
import { ChartLayout } from '@twaice-fe/frontend/shared/utilities';
import { AggregationType, HealthRequestParameters, KPI, StorageLevel } from '@twaice-fe/shared/models';
import { DistributionKpisEnum, KPIChartData, KpiDistributionInterface } from '../models/kpis-distribution.models';
// fixme: importing StorageLevelIdentifierUtils from '@twaice-fe/frontend/shared/services' fails the test.
// eslint-disable-next-line @nx/enforce-module-boundaries
import { StorageLevelIdentifierUtils } from 'libs/frontend/shared/services/src/lib/storage-level-identifier.utils';

export const healthKpisLatestDataRequestConfig: HealthRequestParameters[] = [
  {
    kpiName: DistributionKpisEnum.STATE_OF_HEALTH_CAPACITY,
    kpi: KPI.STATE_OF_HEALTH_CAPACITY,
    aggregationType: AggregationType.NONE,
  },
  {
    kpiName: DistributionKpisEnum.EQUIVALENT_FULL_CYCLES,
    kpi: KPI.EQUIVALENT_FULL_CYCLES,
    aggregationType: AggregationType.NONE,
  },
  {
    kpiName: DistributionKpisEnum.DIRECT_CURRENT_RESISTANCE,
    kpi: KPI.DIRECT_CURRENT_RESISTANCE,
    aggregationType: AggregationType.NONE,
    multiply: true,
    multiplyBy: 1000,
  },
];

export const healthKpisDataRequestConfig: HealthRequestParameters[] = [
  {
    kpiName: 'cellVoltageSpread',
    kpi: KPI.CELL_VOLTAGE_SPREADS,
    aggregationType: AggregationType.MAX,
    multiply: true,
    multiplyBy: 1000,
  },
  {
    kpiName: 'moduleTemperatureSpread',
    kpi: KPI.MODULE_TEMPERATURE_SPREADS,
    aggregationType: AggregationType.MAX,
  },
];

export const kpisChartLayoutConfig: Record<DistributionKpisEnum, ChartLayout> = {
  sohC: {
    title: 'Latest State of Health',
    numberFormat: '1.1-1',
    xTickValuesPrecision: 1,
    yLabel: 'Number of ' + StorageLevelIdentifierUtils.toLevelString(StorageLevel.String),
    xLabel: 'Distribution (<soc_unit>)',
    tooltipTitle: 'SoH',
  },
  efc: {
    title: 'Equivalent Full Cycles',
    numberFormat: '1.0-0',
    xTickValuesPrecision: 0,
    yLabel: 'Number of ' + StorageLevelIdentifierUtils.toLevelString(StorageLevel.String),
    xLabel: 'Distribution by latest values',
    tooltipTitle: 'EFC',
  },
  dcr: {
    title: 'Latest DC Resistance',
    numberFormat: '1.1-1',
    xTickValuesPrecision: 1,
    yLabel: 'Number of ' + StorageLevelIdentifierUtils.toLevelString(StorageLevel.String),
    xLabel: 'Distribution (mΩ)',
    tooltipTitle: 'DCR',
  },
};

export const kpisChartInfoConfig: Record<DistributionKpisEnum, AggregationType[]> = {
  sohC: [AggregationType.MIN, AggregationType.MAX, AggregationType.NONE],
  efc: [AggregationType.MIN, AggregationType.MAX, AggregationType.NONE],
  dcr: [AggregationType.MIN, AggregationType.MAX, AggregationType.NONE],
};

export const initKpisChartDataConfig: Record<DistributionKpisEnum, KPIChartData> = {
  sohC: {},
  efc: {},
  dcr: {},
};

export const initKpisChartDataInfoConfig: Record<DistributionKpisEnum, ChartInfoItemContent[] | null> = {
  sohC: null,
  efc: null,
  dcr: null,
};

export const initKpisChartMissingDataInfoConfig: Record<DistributionKpisEnum, ChartMissingDataContent | null> = {
  sohC: null,
  efc: null,
  dcr: null,
};

export const sortDataByConfig = (
  data: KpiDistributionInterface[],
  key: keyof KpiDistributionInterface,
  direction: 'asc' | 'desc'
): KpiDistributionInterface[] => {
  const isAscending = direction === 'asc';

  return [...data].sort((a, b) => {
    const valA = a[key];
    const valB = b[key];

    if (valA === undefined) return isAscending ? -1 : 1;
    if (valB === undefined) return isAscending ? 1 : -1;

    if (typeof valA === 'number' && typeof valB === 'number') {
      return isAscending ? valA - valB : valB - valA;
    }

    if (typeof valA === 'string' && typeof valB === 'string') {
      return isAscending
        ? valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' })
        : valB.localeCompare(valA, undefined, { numeric: true, sensitivity: 'base' });
    }

    return 0; // Return a neutral value if unexpected type encountered or if both values are of the same type but not number or string.
  });
};
